import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Video, VideoOff, Mic, MicOff, PhoneOff, Users, Copy, CheckCircle, Music } from 'lucide-react';
import { toast } from 'sonner';
import { useVideo } from '../context/VideoContext';
import AdminFreeAccessBar, { generateStudioRoomCode } from '../components/admin/AdminFreeAccessBar';
import { rememberRoom } from '../lib/recentRooms';

/**
 * SimpleVideoRoom - Uses the global VideoContext for persistent connections
 * This way, when you navigate to Studio, the video stays connected
 */
const SimpleVideoRoom = () => {
  const { roomCode } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Detect host from URL path
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const lastSegment = pathSegments[pathSegments.length - 1];
  const isHostFromUrl = lastSegment === 'host';
  
  // Use the global video context
  const {
    connected,
    peerConnected,
    localStream,
    remoteStream,
    micEnabled,
    videoEnabled,
    status,
    roomCode: currentRoomCode,
    isHost,
    joinRoom,
    leaveRoom,
    toggleMic,
    toggleVideo
  } = useVideo();
  
  const [roomId, setRoomId] = useState('');
  const [copied, setCopied] = useState(false);
  const [joining, setJoining] = useState(false);
  
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  
  // Set room ID from URL
  useEffect(() => {
    if (roomCode) {
      const upper = roomCode.toUpperCase();
      setRoomId(upper);
      // Remember this room in the admin recent-rooms list
      rememberRoom(upper, { role: isHostFromUrl ? 'host' : 'guest' });
    } else {
      const newCode = Math.random().toString(36).substring(2, 8).toUpperCase();
      setRoomId(newCode);
      rememberRoom(newCode, { role: 'host' });
      window.history.replaceState({}, '', `/video-room/${newCode}/host`);
    }
  }, [roomCode, isHostFromUrl]);
  
  // Set video streams when they change
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
      localVideoRef.current.play().catch(e => console.log('Local play:', e));
    }
  }, [localStream, connected]);
  
  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
      remoteVideoRef.current.play().catch(e => console.log('Remote play:', e));
    }
  }, [remoteStream, peerConnected]);
  
  const copyClientLink = () => {
    const link = `${window.location.origin}/video-room/${roomId}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Client link copied!');
    setTimeout(() => setCopied(false), 3000);
  };
  
  const handleJoinRoom = async () => {
    if (!roomId) {
      toast.error('No room code!');
      return;
    }
    setJoining(true);
    const success = await joinRoom(roomId, isHostFromUrl);
    setJoining(false);
    if (!success) {
      toast.error('Failed to join room');
    }
  };
  
  const handleLeaveRoom = () => {
    leaveRoom();
    navigate('/');
  };
  
  const enterStudio = () => {
    // Navigate to studio - video stays connected via context!
    navigate(`/admin/studio-session/${roomId}`);
  };

  // Pre-join screen (not connected yet)
  if (!connected) {
    return (
      <div className={`min-h-screen ${isHostFromUrl ? 'bg-gradient-to-br from-green-900 via-black to-green-900' : 'bg-gradient-to-br from-blue-900 via-black to-blue-900'}`}>
        <AdminFreeAccessBar
          title={`Admin — ${isHostFromUrl ? 'Video Room (Host)' : 'Video Room (Guest)'} Free Access`}
          subtitle="Spin up a fresh room or open the DAW without leaving this page."
          testid="admin-bar-video-room"
          primary={{
            label: 'Fresh Live Room',
            icon: Video,
            onClick: () => {
              const code = generateStudioRoomCode();
              window.location.href = `/video-room/${code}/host`;
            },
          }}
          secondary={{
            label: 'Open DAW',
            onClick: () => { window.location.href = '/admin/studio-session'; },
          }}
        />
        <div className="flex items-center justify-center p-4 min-h-[calc(100vh-74px)]">
        <div className="bg-gray-800/80 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full border-2 border-gray-600">
          
          {/* Role indicator */}
          <div className={`text-center py-6 rounded-2xl mb-6 ${isHostFromUrl ? 'bg-green-600' : 'bg-blue-600'}`}>
            <span className="text-5xl mb-2 block">{isHostFromUrl ? '👑' : '👤'}</span>
            <span className="text-3xl font-black text-white">
              {isHostFromUrl ? 'HOST' : 'CLIENT'}
            </span>
          </div>
          
          <h1 className="text-2xl font-bold text-white text-center mb-2">Escola de Samba Casa Samba Studio</h1>
          
          <div className="bg-black/50 rounded-xl p-4 mb-4">
            <p className="text-gray-400 text-sm">Room Code</p>
            <p className="text-3xl font-mono font-bold text-yellow-400">{roomId || 'Loading...'}</p>
          </div>
          
          {isHostFromUrl && (
            <div className="bg-green-900/50 border-2 border-green-500 rounded-xl p-4 mb-4">
              <p className="text-green-300 font-bold mb-2">📤 Send this to your CLIENT:</p>
              <div className="bg-black rounded p-2 mb-2">
                <code className="text-green-400 text-sm break-all">
                  {window.location.origin}/video-room/{roomId}
                </code>
              </div>
              <button
                onClick={copyClientLink}
                className="w-full py-2 bg-green-600 hover:bg-green-500 rounded-lg text-white font-bold flex items-center justify-center gap-2"
              >
                {copied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                {copied ? 'Copied!' : 'Copy Client Link'}
              </button>
            </div>
          )}
          
          {!isHostFromUrl && (
            <div className="bg-blue-900/50 border-2 border-blue-500 rounded-xl p-4 mb-4">
              <p className="text-blue-300">
                You will connect as <strong>CLIENT</strong>.
              </p>
            </div>
          )}
          
          <button
            onClick={handleJoinRoom}
            disabled={!roomId || joining}
            className={`w-full py-4 font-bold text-xl rounded-xl text-white ${
              isHostFromUrl 
                ? 'bg-green-600 hover:bg-green-500' 
                : 'bg-blue-600 hover:bg-blue-500'
            } ${(!roomId || joining) ? 'opacity-50' : ''}`}
          >
            {joining ? 'Connecting...' : `JOIN AS ${isHostFromUrl ? 'HOST 👑' : 'CLIENT 👤'}`}
          </button>
          
          {status && status !== 'Not connected' && (
            <p className="text-center text-yellow-400 mt-4">{status}</p>
          )}
        </div>
        </div>
      </div>
    );
  }

  // In-call screen (connected)
  return (
    <div className="min-h-screen bg-black text-white">
      <AdminFreeAccessBar
        title="Admin — In-Call · Free Access"
        subtitle="You're in a live room. Spin up a fresh one or open the DAW any time."
        testid="admin-bar-video-room-live"
        primary={{
          label: 'Fresh Live Room',
          icon: Video,
          onClick: () => {
            const code = generateStudioRoomCode();
            window.location.href = `/video-room/${code}/host`;
          },
        }}
        secondary={{
          label: 'Open DAW',
          onClick: () => { window.location.href = '/admin/studio-session'; },
        }}
      />
      <div className="relative h-[calc(100vh-140px)]">
        {/* Remote Video */}
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          className="w-full h-full object-cover bg-gray-900"
        />
        
        {/* Waiting overlay */}
        {!peerConnected && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80">
            <div className={`w-24 h-24 rounded-full ${isHost ? 'bg-green-600' : 'bg-blue-600'} flex items-center justify-center mb-4 animate-pulse`}>
              <Users className="w-12 h-12" />
            </div>
            <p className="text-2xl font-bold mb-2">{status}</p>
            <p className={`text-xl ${isHost ? 'text-green-400' : 'text-blue-400'}`}>
              You are: {isHost ? 'HOST 👑' : 'CLIENT 👤'}
            </p>
            <p className="text-gray-400 mt-1">Room: {currentRoomCode}</p>
            
            {isHost && (
              <div className="mt-6 bg-gray-800 rounded-xl p-4 max-w-md">
                <p className="text-green-400 mb-2 font-bold">Send to client:</p>
                <div className="flex gap-2">
                  <code className="flex-1 bg-black px-3 py-2 rounded text-green-400 text-sm">
                    {window.location.origin}/video-room/{currentRoomCode}
                  </code>
                  <button onClick={copyClientLink} className="p-2 bg-green-600 rounded">
                    <Copy className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Local video PIP */}
        <div className={`absolute bottom-4 right-4 w-48 aspect-video rounded-xl overflow-hidden border-4 ${isHost ? 'border-green-500' : 'border-blue-500'} bg-gray-800`}>
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
            style={{ transform: 'scaleX(-1)' }}
          />
          <div className={`absolute bottom-1 left-1 ${isHost ? 'bg-green-600' : 'bg-blue-600'} px-2 py-1 rounded text-xs font-bold`}>
            {isHost ? 'HOST (You)' : 'CLIENT (You)'}
          </div>
        </div>
        
        {/* Status */}
        <div className="absolute top-4 left-4 bg-black/70 rounded-lg px-4 py-2 flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full animate-pulse ${peerConnected ? 'bg-green-500' : 'bg-yellow-500'}`} />
          <span className="font-bold">
            {peerConnected ? 'VIDEO CONNECTED' : 'WAITING FOR PEER'}
          </span>
          <span className="text-gray-400">| {isHost ? 'HOST' : 'CLIENT'} | {currentRoomCode}</span>
        </div>
      </div>
      
      {/* Controls */}
      <div className="h-[140px] bg-gray-900 flex flex-col items-center justify-center gap-3">
        <div className="flex items-center gap-4">
          <button onClick={toggleMic} className={`w-14 h-14 rounded-full ${micEnabled ? 'bg-gray-700' : 'bg-red-600'}`}>
            {micEnabled ? <Mic className="w-6 h-6 mx-auto" /> : <MicOff className="w-6 h-6 mx-auto" />}
          </button>
          <button onClick={toggleVideo} className={`w-14 h-14 rounded-full ${videoEnabled ? 'bg-gray-700' : 'bg-red-600'}`}>
            {videoEnabled ? <Video className="w-6 h-6 mx-auto" /> : <VideoOff className="w-6 h-6 mx-auto" />}
          </button>
          <button onClick={handleLeaveRoom} className="w-14 h-14 rounded-full bg-red-600">
            <PhoneOff className="w-6 h-6 mx-auto" />
          </button>
          
          {/* Enter Studio button - HOST only, when peer connected */}
          {peerConnected && isHost && (
            <button onClick={enterStudio} className="ml-4 px-6 py-3 bg-amber-500 hover:bg-amber-400 rounded-xl font-bold text-black flex items-center gap-2">
              <Music className="w-5 h-5" />
              Enter Studio
            </button>
          )}
        </div>
        <p className="text-gray-500 text-sm">{status}</p>
      </div>
    </div>
  );
};

export default SimpleVideoRoom;
